import React, { useState } from "react";
import {
  Box,
  Button,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  InputGroup,
  InputLeftElement,
  Input,
  Text,
} from "@chakra-ui/react";
import { ChevronDownIcon, SearchIcon } from "@chakra-ui/icons";

/**
 * SearchableDropdown - A reusable searchable dropdown component
 * 
 * @param {Array} options - Array of objects to display in dropdown
 * @param {string} value - Currently selected value (id/key)
 * @param {Function} onChange - Callback function when selection changes (receives full object)
 * @param {string} placeholder - Placeholder text for the button
 * @param {string} searchPlaceholder - Placeholder text for search input
 * @param {Function} getOptionLabel - Function to get display label from option object
 * @param {Function} getOptionValue - Function to get unique value from option object
 * @param {Function} getOptionSubLabel - Optional function to get secondary text
 * @param {Function} filterOptions - Optional custom filter function
 * @param {boolean} isDisabled - Disable the dropdown
 * @param {string} size - Size of the button ("sm", "md", "lg")
 * @param {string} variant - Button variant
 * @param {boolean} matchWidth - Whether MenuList should match button width
 * @param {string} maxHeight - Max height of the dropdown list
 * @param {ReactNode} noOptionsMessage - Custom message when no options found
 */
const SearchableDropdown = ({
  options = [],
  value,
  onChange,
  placeholder = "Select option",
  searchPlaceholder = "Search...",
  getOptionLabel = (option) => option.name || option.label || "",
  getOptionValue = (option) => option.id || option.value,
  getOptionSubLabel = null,
  filterOptions = null,
  isDisabled = false,
  size = "md",
  variant = "outline",
  matchWidth = true,
  maxHeight = "250px",
  noOptionsMessage = "No options found",
}) => {
  const [searchTerm, setSearchTerm] = useState("");

  // Find the selected option to display its label
  const selectedOption = options.find(
    (opt) => getOptionValue(opt) === value
  );
  const displayLabel = selectedOption
    ? getOptionLabel(selectedOption)
    : placeholder;

  // Filter options based on search term
  const filteredOptions = filterOptions
  ? filterOptions(options, searchTerm)
  : options.filter((option) => {
      const label = (getOptionLabel(option) || "").toLowerCase();
      const subLabel = getOptionSubLabel
        ? (getOptionSubLabel(option) || "").toLowerCase()
        : "";
      const term = searchTerm.toLowerCase();

      return label.includes(term) || subLabel.includes(term);
    });


  const handleSelect = (option) => {
    onChange(option);
    setSearchTerm(""); // Reset search after selection
  };

  return (
    <Menu closeOnSelect={true} matchWidth={matchWidth}>
      <MenuButton
        as={Button}
        rightIcon={<ChevronDownIcon />}
        w="100%"
        size={size}
        variant={variant}
        isDisabled={isDisabled}
        textAlign="left"
      >
        {displayLabel}
      </MenuButton>

      <MenuList maxH={maxHeight} overflowY="auto" p={2}>
        {/* Search Input */}
        <InputGroup mb={2}>
          <InputLeftElement pointerEvents="none">
            <SearchIcon color="gray.400" />
          </InputLeftElement>
          <Input
            placeholder={searchPlaceholder}
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="sm"
            autoFocus
            onClick={(e) => e.stopPropagation()}
            onKeyDown={(e) => e.stopPropagation()}
          />
        </InputGroup>

        {/* Options List */}
        {filteredOptions.length > 0 ? (
          filteredOptions.map((option) => {
            const optionValue = getOptionValue(option);
            const optionLabel = getOptionLabel(option);
            const optionSubLabel = getOptionSubLabel
              ? getOptionSubLabel(option)
              : null;

            return (
              <MenuItem
                key={optionValue}
                onClick={() => handleSelect(option)}
                bg={value === optionValue ? "blue.50" : "transparent"}
              >
                <Box>
                  <Text fontWeight="medium">{optionLabel}</Text>
                  {optionSubLabel && (
                    <Text fontSize="xs" color="gray.500">
                      {optionSubLabel}
                    </Text>
                  )}
                </Box>
              </MenuItem>
            );
          })
        ) : (
          <Box p={3} textAlign="center" color="gray.500">
            {noOptionsMessage}
          </Box>
        )}
      </MenuList>
    </Menu>
  );
};

export default SearchableDropdown;